import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import useAdminProfile from '../../hooks/useAdminProfile';
import SettlementPcFrame, { accessMessage } from './SettlementPcFrame';
import {
  MESSAGES,
  formatCount,
  formatWon,
  isValidPayoutMonth,
  statementTitle,
  toSaleMonth,
} from './settlementStatement.util';
import useSettlementStatement from './useSettlementStatement';

function RowsTable({ rows }) {
  return (
    <table className="table_box_01 settlement-pc__table">
      <colgroup>
        <col />
        <col width="130px" />
        <col width="70px" />
      </colgroup>
      <thead>
        <tr>
          <th style={{ textAlign: 'left' }}>항목</th>
          <th style={{ textAlign: 'right' }}>금액</th>
          <th style={{ textAlign: 'right' }}>건수</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.key} className={row.isSub ? 'settlement-pc__sub' : undefined}>
            <td style={{ textAlign: 'left' }}>
              {row.sign && (
                <span
                  className={`settlement-pc__sign ${
                    row.sign === '+' ? 'settlement-pc__sign--plus' : 'settlement-pc__sign--minus'
                  }`}
                  aria-label={row.sign === '+' ? '더하기' : '빼기'}
                >
                  {row.sign}
                </span>
              )}
              {row.isSub ? `└ ${row.label}` : row.label}
            </td>
            <td className="settlement-pc__amount">{formatWon(row.amount)}</td>
            <td className="settlement-pc__count">{formatCount(row.count)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatementBody({ view }) {
  return (
    <>
      <section className="settlement-pc__summary" aria-label="요약">
        <div className="settlement-pc__card settlement-pc__card--primary">
          <span className="settlement-pc__card-label">지급예정 금액</span>
          <span className="settlement-pc__card-amount">{formatWon(view.payoutAmount)}</span>
        </div>
        <div className="settlement-pc__card">
          <span className="settlement-pc__card-label">지급예정일</span>
          <span className="settlement-pc__card-value">{view.payoutDue}</span>
        </div>
        <div className="settlement-pc__card">
          <span className="settlement-pc__card-label">상점명 (아이디)</span>
          <span className="settlement-pc__card-value">{view.storeName}</span>
          {view.adminId && <span className="settlement-pc__card-sub">{view.adminId}</span>}
        </div>
        <div className="settlement-pc__card">
          <span className="settlement-pc__card-label">이용기간</span>
          <span className="settlement-pc__card-value">{view.usagePeriod}</span>
        </div>
      </section>

      <section>
        <h3 className="settlement-pc__heading">정산 내역</h3>
        <RowsTable rows={view.settlementRows} />
        <div className="settlement-pc__total">
          <span className="settlement-pc__total-text">
            <strong>지급예정 금액</strong>
            <span>{view.formula}</span>
          </span>
          <span className="settlement-pc__total-amount">{formatWon(view.payoutAmount)}</span>
        </div>
      </section>
    </>
  );
}

/** 정산명세서 상세 PC(/:storeNo/settlement-statement/:payoutMonth). 주소의 월은 정산월이다. */
export default function SettlementDetailPc() {
  const { storeNo, payoutMonth } = useParams();
  const navigate = useNavigate();
  const profileState = useAdminProfile();
  const blocked = profileState.loading
    ? MESSAGES.loading
    : accessMessage(profileState, storeNo, MESSAGES);
  const validMonth = isValidPayoutMonth(payoutMonth);
  // 정산월이 올바르고 로그인 상점과 일치할 때만 조회한다.
  const result = useSettlementStatement(payoutMonth, validMonth && blocked === null);
  const listPath = `/${storeNo}/settlement-statement`;

  useEffect(() => {
    if (!validMonth) navigate(listPath, { replace: true });
  }, [navigate, listPath, validMonth]);

  if (!validMonth) return null;

  const message = () => {
    if (blocked !== null) return blocked;
    if (result.status === 'loading') return MESSAGES.loading;
    if (result.status === 'empty') return MESSAGES.empty;
    if (result.status === 'message') return result.message;
    return '';
  };

  return (
    <SettlementPcFrame storeNo={storeNo}>
      <div className="settlement-pc__head">
        <h2 className="tit_01 settlement-pc__title">
          {statementTitle(toSaleMonth(payoutMonth))}
        </h2>
        <button
          type="button"
          className="button_04 button_gray_line"
          onClick={() => navigate(listPath)}
        >
          목록
        </button>
      </div>

      {blocked === null && result.status === 'ready' ? (
        <StatementBody view={result.view} />
      ) : (
        message() && (
          <article className="form_card list_card">
            <p className="settlement-pc__message">{message()}</p>
          </article>
        )
      )}
    </SettlementPcFrame>
  );
}
